import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { ConfirmModalComponent } from '../../../shared/components/ui/confirm-modal/confirm-modal.component';
import { DataStateComponent } from '../../../shared/components/ui/data-state/data-state.component';
import { PageHeaderComponent } from '../../../shared/components/ui/page-header/page-header.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { PermissionsService } from '../../../shared/services/permissions.service';
import { ResourceDTO } from '../../../shared/types/permission.types';

@Component({
  selector: 'app-resource-detail',
  imports: [
    CommonModule,
    RouterModule,
    PageHeaderComponent,
    DataStateComponent,
    ConfirmModalComponent,
  ],
  templateUrl: './resource-detail.component.html',
})
export class ResourceDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly permissionsService = inject(PermissionsService);
  private readonly destroyRef = inject(DestroyRef);

  readonly resource = signal<ResourceDTO | null>(null);
  readonly loading = signal(true);
  readonly loadError = signal<string | null>(null);
  readonly deleteOpen = signal(false);
  readonly deleting = signal(false);
  readonly deleteError = signal<string | null>(null);

  private id = '';

  ngOnInit(): void {
    this.id = this.route.snapshot.paramMap.get('id') ?? '';
    if (!this.id) {
      this.loadError.set('Identifiant de resource invalide.');
      this.loading.set(false);
      return;
    }
    this.fetch();
  }

  fetch(): void {
    this.loading.set(true);
    this.loadError.set(null);
    this.permissionsService
      .getResource(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (r) => { this.resource.set(r); this.loading.set(false); },
        error: (err: unknown) => {
          this.loading.set(false);
          this.loadError.set(
            toErrorMessage(err, 'Impossible de charger cette resource.'),
          );
        },
      });
  }

  openDelete(): void { this.deleteError.set(null); this.deleteOpen.set(true); }
  closeDelete(): void { this.deleteOpen.set(false); }
  confirmDelete(): void {
    this.deleting.set(true);
    this.deleteError.set(null);
    this.permissionsService.deleteResource(this.id).subscribe({
      next: () => {
        this.deleting.set(false);
        this.deleteOpen.set(false);
        void this.router.navigate(['/permissions/resources']);
      },
      error: (err: unknown) => {
        this.deleting.set(false);
        this.deleteError.set(toErrorMessage(err, 'errors.deleteFailed'));
      },
    });
  }
}
